import { useSignalR } from '@/composables';
import {
    useConversationUpdates,
    type ConversationChangedDto,
} from '@/composables/useConversationUpdates';
import { useIdentityStore } from '@/store/identity.store';
import { App } from 'vue';

type SignalRHandle = ReturnType<typeof useSignalR>;

// Module-scoped guard so an accidental second `app.use(...)` cannot spin up a
// duplicate hub connection / duplicate handlers. The live handle is retained so
// the HMR dispose hook below can tear the connection down before the replacement
// module re-installs — a plain boolean guard does NOT survive HMR because the
// new module instance starts with `installed = false` while the old connection
// lingers, leaking a second `/ws/conversations` socket and duplicate listeners.
let installed = false;
let activeSignalR: SignalRHandle | null = null;

const teardown = async (): Promise<void> => {
    const handle = activeSignalR;
    activeSignalR = null;
    installed = false;

    if (!handle) return;

    try {
        await handle.stopConnection();
    } catch (error) {
        console.error('Conversation updates SignalR teardown failed', error);
    }

    // Reset the shared bridge so the next install starts from a clean,
    // disconnected state rather than inheriting a stale "connected" flag.
    useConversationUpdates().setConnected(false);
};

export default {
    install: async (app: App) => {
        if (installed) return;
        installed = true;

        const identityStore = useIdentityStore();
        const bridge = useConversationUpdates();

        const signalR = useSignalR('/ws/conversations', {
            ConversationChanged: (payload: ConversationChangedDto) => {
                const currentUserId = identityStore.data?.profile?.id ?? null;
                bridge.handleConversationChanged(payload, currentUserId);
            },
        });
        activeSignalR = signalR;

        try {
            await signalR.startConnection();
            bridge.setConnected(true);
        } catch (error) {
            // Graceful degradation: the inbox still works via manual refresh.
            bridge.setConnected(false);
            console.error('Conversation updates SignalR connection failed', error);
        }

        app.config.globalProperties.$conversationUpdates = signalR;
    },
};

// HMR-safe cleanup: when Vite swaps this module, stop the existing hub
// connection and reset the install guard so the replacement module can install
// exactly one fresh connection without overlapping the previous one.
if (import.meta.hot) {
    import.meta.hot.dispose(() => {
        void teardown();
    });
}
